namespace Portico.Services {

	import pm = Portico.Models;
	import pmi = Portico.Models.Interfaces;
	import pms = Portico.Models.Setup;

	import Inject = Portico.Decorators.Inject;
	import Service = Portico.Decorators.Service;

    export interface ISetupService {
		dataGroupService: IDataGroupService;
		dataGroupTypeService: IDataGroupTypeService;
		dataRowService: IDataRowService;
		dataTableService: IDataTableService;
		dataTypeService: IDataTypeService;
		portalChildService: IPortalChildService;
		portalDataGroupService: IPortalDataGroupService;
		portalGroupService: IPortalGroupService;
		portalService: IPortalService;

		// data column functions
		addDataColumn(dataTable: pms.DataTable, dataColumn?: pms.DataColumn): void;
		removeDataColumn(dataTable: pms.DataTable, dataColumn: pms.DataColumn): void;
		// data group functions
		removeDataGroup(dataGroup: pms.DataGroup, dataGroups?: pms.DataGroup[]): angular.IPromise<boolean>;
		saveDataGroup(dataGroup: pms.DataGroup, dataGroups?: pms.DataGroup[]): angular.IPromise<pms.DataGroup>;
		// data group type functions
		removeDataGroupType(dataGroupType: pms.DataGroupType, dataGroupTypes?: pms.DataGroupType[]): angular.IPromise<boolean>;
		saveDataGroupType(dataGroupType: pms.DataGroupType, dataGroupTypes?: pms.DataGroupType[]): angular.IPromise<pms.DataGroupType>;
		// data row functions
		createDataRow(dataGroupId: string, dataTableId: string, dataRow?: pms.DataRow): pms.DataRow;
		fixDataRow(dataTable: pms.DataTable, dataRow: pms.DataRow): void;
		removeDataRow(dataRow: pms.DataRow, dataRows?: pms.DataRow[]): angular.IPromise<boolean>;
		saveDataRow(dataRow: pms.DataRow, dataRows?: pms.DataRow[]): angular.IPromise<pms.DataRow>;
		// data table functions
		removeDataTable(dataTable: pms.DataTable, dataTables?: pms.DataTable[]): angular.IPromise<boolean>;
		saveDataTable(dataTable: pms.DataTable, dataTables?: pms.DataTable[]): angular.IPromise<pms.DataTable>;
		// portal functions
		importPortal(upload: any): void;
		removePortal(portal: pms.Portal, portals?: pms.Portal[]): angular.IPromise<boolean>;
		savePortal(portal: pms.Portal, portals?: pms.Portal[], copyFrom?: string): angular.IPromise<pms.Portal>;
		// portal child functions
		removePortalChild(portalChild: pms.PortalChild, portalChildren?: pms.PortalChild[]): angular.IPromise<boolean>;
		savePortalChild(portalChild: pms.PortalChild, portalChildren?: pms.PortalChild[]): angular.IPromise<pms.PortalChild>;
		// portal data group functions
		flattenPortalDataGroups(portalDataGroups: pms.PortalDataGroup[]): angular.IPromise<boolean>;
		removePortalDataGroup(portalDataGroup: pms.PortalDataGroup, portalDataGroups?: pms.PortalDataGroup[]): angular.IPromise<boolean>;
		savePortalDataGroup(portalDataGroup: pms.PortalDataGroup, portalDataGroups?: pms.PortalDataGroup[], dataGroups?: pms.DataGroup[]): angular.IPromise<pms.PortalDataGroup>;
		// portal group functions
		removePortalGroup(portalGroup: pms.PortalGroup, portalGroups?: pms.PortalGroup[]): angular.IPromise<boolean>;
		savePortalGroup(portalGroup: pms.PortalGroup, portalGroups?: pms.PortalGroup[]): angular.IPromise<pms.PortalGroup>;
	}

	@Service(
		'app',
		'setupService'
	)
	@Inject(
		'$q',
		'Upload',
		'dataGroupService',
		'dataGroupTypeService',
		'dataRowService',
		'dataTableService',
		'dataTypeService',
		'modalService',
		'portalChildService',
		'portalDataGroupService',
		'portalGroupService',
		'portalService',
		'serverConstants'
	)
	export class SetupService implements ISetupService {
		constructor(
			private $q: angular.IQService,
			private $upload: any,
			public dataGroupService: IDataGroupService,
			public dataGroupTypeService: IDataGroupTypeService,
			public dataRowService: IDataRowService,
			public dataTableService: IDataTableService,
			public dataTypeService: IDataTypeService,
			public modalService: IModalService,
			public portalChildService: IPortalChildService,
			public portalDataGroupService: IPortalDataGroupService,
			public portalGroupService: IPortalGroupService,
			public portalService: IPortalService,
			private serverConstants: pmi.IServerConstants
        ) { }

		// data column functions

		addDataColumn(dataTable: pms.DataTable, dataColumn: pms.DataColumn = null): void {
			if (!dataTable.dataColumns) {
				dataTable.dataColumns = [];
			}

			dataTable.dataColumns.push(dataColumn || new pms.DataColumn());
		}

		removeDataColumn(dataTable: pms.DataTable, dataColumn: pms.DataColumn): void {
			dataTable.dataColumns = dataTable.dataColumns.filter((dc: pms.DataColumn) => {
				return dc !== dataColumn;
			});
		}

		// data group functions

		removeDataGroup(dataGroup: pms.DataGroup, dataGroups: pms.DataGroup[] = null): angular.IPromise<boolean> {
			return this.dataGroupService.delete(dataGroup).then((response: boolean) => {
				if (dataGroups) {
					for (let i = dataGroups.length - 1; i >= 0; i--) {
						if (dataGroups[i].id === dataGroup.id) {
							dataGroups.splice(i, 1);
						}
					}
				}

				return response;
			});
		}

		saveDataGroup(dataGroup: pms.DataGroup, dataGroups: pms.DataGroup[] = null): angular.IPromise<pms.DataGroup> {
			let duplicate = false;

			// check for a duplicate dataGroup name
			if (dataGroups) {
				dataGroups.forEach((pg: pms.DataGroup) => {
					if (pg.id !== dataGroup.id && pg.name === dataGroup.name) {
						duplicate = true;
					}
				});

				if (duplicate) {
					return this.$q.when(null);
				}
			}

			// save dataGroup
			return this.dataGroupService.save(dataGroup).then((response: pms.DataGroup) => {
				if (dataGroups) {
					if (dataGroup.id) {
						// update dataGroup
						for (let i = dataGroups.length - 1; i >= 0; i--) {
							if (dataGroups[i].id === dataGroup.id) {
								dataGroups[i] = response;
							}
						}
					} else {
						// insert dataGroup
						dataGroups.push(response);
					}
				}

				return response;
			});
		}

		// data group type functions

		removeDataGroupType(dataGroupType: pms.DataGroupType, dataGroupTypes: pms.DataGroupType[] = null): angular.IPromise<boolean> {
			return this.dataGroupTypeService.delete(dataGroupType).then((response: boolean) => {
				if (dataGroupTypes) {
					for (let i = dataGroupTypes.length - 1; i >= 0; i--) {
						if (dataGroupTypes[i].id === dataGroupType.id) {
							dataGroupTypes.splice(i, 1);
						}
					}
				}

				return response;
			});
		}

		saveDataGroupType(dataGroupType: pms.DataGroupType, dataGroupTypes: pms.DataGroupType[] = null): angular.IPromise<pms.DataGroupType> {
			let duplicate = false;

			// check for a duplicate dataGroupType name
			if (dataGroupTypes) {
				dataGroupTypes.forEach((pg: pms.DataGroupType) => {
					if (pg.id !== dataGroupType.id && pg.name === dataGroupType.name) {
						duplicate = true;
					}
				});

				if (duplicate) {
					return this.$q.when(null);
				}
			}

			// save dataGroupType
			return this.dataGroupTypeService.save(dataGroupType).then((response: pms.DataGroupType) => {
				if (dataGroupTypes) {
					if (dataGroupType.id) {
						// update dataGroupType
						for (let i = dataGroupTypes.length - 1; i >= 0; i--) {
							if (dataGroupTypes[i].id === dataGroupType.id) {
								dataGroupTypes[i] = response;
							}
						}
					} else {
						// insert dataGroupType
						dataGroupTypes.push(response);
					}
				}

				return response;
			});
		}

		// data row functions

		createDataRow(dataGroupId: string, dataTableId: string, dataRow: pms.DataRow = null): pms.DataRow {
			return dataRow ? new pms.DataRow(dataRow) : new pms.DataRow(<pms.IDataRow>{ dataGroupId: dataGroupId, dataTableId: dataTableId });
		}

		fixDataRow(dataTable: pms.DataTable, dataRow: pms.DataRow): void {
			// remove invalid values
			for (let i = dataRow.dataValues.length - 1; i >= 0; i--) {
				const matches = dataTable.dataColumns.filter((dc: pms.DataColumn) => {
					return dc.id === dataRow.dataValues[i].dataColumnId;
				});

				if (!matches.length) {
					dataRow.dataValues[i].isDeleted = true;
					// this.dataRow.dataValues.splice(i, 1);
				}
			}

			// add missing values
			dataTable.dataColumns.forEach((dc: pms.DataColumn) => {
				if (dataRow.getDataValue(dc.id) === null) {
					dataRow.dataValues.push(new pms.DataValue({ id: null, dataRowId: dataRow.id, dataColumnId: dc.id, value: null, createdBy: null, createdOn: null, isDeleted: false }));
				}
			});
		}

		removeDataRow(dataRow: pms.DataRow, dataRows: pms.DataRow[] = null): angular.IPromise<boolean> {
			return this.dataRowService.delete(dataRow).then((response: boolean) => {
				if (dataRows) {
					for (let i = dataRows.length - 1; i >= 0; i--) {
						if (dataRows[i].id === dataRow.id) {
							dataRows.splice(i, 1);
						}
					}
				}

				return response;
			});
		}

		saveDataRow(dataRow: pms.DataRow, dataRows: pms.DataRow[] = null): angular.IPromise<pms.DataRow> {
			let duplicate = false;

			// check for a duplicate data key
			if (dataRows) {
				dataRows.forEach((dr: pms.DataRow) => {
					if (dr.id !== dataRow.id && dr.dataGroupId === dataRow.dataGroupId && dr.dataTableId === dataRow.dataTableId && dr.key === dataRow.key) {
						duplicate = true;
					}
				});

				if (duplicate) {
					return this.$q.when(null);
				}
			}

			// save data
			return this.dataRowService.save(dataRow).then((response: pms.DataRow) => {
				if (dataRows) {
					if (dataRow.id) {
						// update data
						for (let i = dataRows.length - 1; i >= 0; i--) {
							if (dataRows[i].id === dataRow.id) {
								dataRows[i] = response;
							}
						}
					} else {
						// insert data
						dataRows.push(response);
					}
				}

				return response;
			});
		}

		// data table functions

		removeDataTable(dataTable: pms.DataTable, dataTables: pms.DataTable[] = null): angular.IPromise<boolean> {
			return this.dataTableService.delete(dataTable).then((response: boolean) => {
				if (dataTables) {
					for (let i = dataTables.length - 1; i >= 0; i--) {
						if (dataTables[i].id === dataTable.id) {
							dataTables.splice(i, 1);
						}
					}
				}

				return response;
			});
		}

		saveDataTable(dataTable: pms.DataTable, dataTables: pms.DataTable[] = null): angular.IPromise<pms.DataTable> {
			let duplicate = false;

			if (dataTables) {
				// check for a duplicate data table name
				dataTables.forEach((dt: pms.DataTable) => {
					if (dt.id !== dataTable.id && dt.name === dataTable.name) {
						duplicate = true;
					}
				});

				if (dataTable.dataColumns) {
					// check for a duplicate data column name
					dataTable.dataColumns.forEach((dc: pms.DataColumn) => {
						const matches = dataTable.dataColumns.filter((dc2: pms.DataColumn) => {
							return dc2.name === dc.name;
						});

						if (matches.length > 1) {
							duplicate = true;
						}
					});
				}

				if (duplicate) {
					return this.$q.when(null);
				}
			}

			// save data table
			return this.dataTableService.save(dataTable).then((response: pms.DataTable) => {
				if (dataTables) {
					if (dataTable.id) {
						// update data table
						for (let i = dataTables.length - 1; i >= 0; i--) {
							if (dataTables[i].id === dataTable.id) {
								dataTables[i] = response;
							}
						}
					} else {
						// insert data table
						dataTables.push(response);
					}
				}

				return response;
			});
		}

		// portal functions

		importPortal(upload: any): angular.IPromise<boolean> {
			// this.modalService.show('spinner');
			document.getElementById('global-spinner-overlay').style.display = 'flex';

			return this.$upload.upload({
				url: this.serverConstants.root + 'Setup/Import',
				data: { file: upload }
			}).then((data, status, headers, config) => {
				return true
			}, (data, status, headers, config) => {
				return false;
			}).finally(() => {
				// this.modalService.hide('spinner');
				document.getElementById('global-spinner-overlay').style.display = 'none';
			});
		}

		removePortal(portal: pms.Portal, portals: pms.Portal[] = null): angular.IPromise<boolean> {
			return this.portalService.getDetailsById(portal.id).then((details) => {
				return this.portalService.delete(portal).then((response: boolean) => {
					if (portals) {
						for (let i = portals.length - 1; i >= 0; i--) {
							if (portals[i].id === portal.id) {
								portals.splice(i, 1);
							}
						}
					}

					const promises: angular.IPromise<any>[] = [];

					details.portalDataGroups.forEach((portalDataGroup) => {
						if (portalDataGroup.dataGroup.isPrivate) {
							promises.push(this.dataGroupService.delete(portalDataGroup.dataGroup));
						}
					});

					return this.$q.all(promises).then(() => {
						return response;
					});
				});
			});
		}

		savePortal(portal: pms.Portal, portals: pms.Portal[] = null, copyFrom: string = null): angular.IPromise<pms.Portal> {
			let duplicate = false;

			// check for a duplicate portal version
			if (portals) {
				portals.forEach((p: pms.Portal) => {
					if (p.id !== portal.id && p.name === portal.name) {
						duplicate = true;
					}
				});

				if (duplicate) {
					return this.$q.when(null);
				}
			}

			// save portal
			return this.portalService.save(portal).then((response: pms.Portal) => {
				if (portals) {
					if (portal.id) {
						// update portal
						for (let i = portals.length - 1; i >= 0; i--) {
							if (portals[i].id === portal.id) {
								portals[i] = response;
							}
						}
					} else {
						// insert portal
						portals.push(response);
					}
				}

				return portal.id ? this.$q.when(response) : this.copyAndSave_portalDetails(response, copyFrom);
			});
		}

		private copyAndSave_portalDetails(portal: pms.Portal, copyFrom: string): angular.IPromise<pms.Portal> {
			let promise = null as angular.IPromise<pms.Portal>;

			if (copyFrom) {
				promise = this.portalService.getDetailsById(copyFrom);
			} else {
				const temp = new pms.Portal(<pms.IPortal>{
					portalDataGroups: [
						new pms.PortalDataGroup(<pms.IPortalDataGroup>{
							dataGroup: new pms.DataGroup(<pms.IDataGroup>{
								id: pm.DATA_GROUP_STANDARD_DEFAULT,
								isPrivate: false
							}),
							priority: 1
						}),
						new pms.PortalDataGroup(<pms.IPortalDataGroup>{
							dataGroup: new pms.DataGroup(<pms.IDataGroup>{
								dataGroupTypeId: pm.DATA_GROUP_TYPE_PORTAL,
								isPrivate: true,
								name: portal.name
							}),
							priority: 9
						})
					]
				});

				promise = this.$q.when(temp);
			}

			return promise.then((original) => {
				let pdgPromise = this.$q.when();

				original.portalDataGroups.forEach((opdg) => {
					pdgPromise = pdgPromise.then((result) => {
						return this.copyAndSave_portalDataGroup(portal.id, portal.name, opdg).then((pdg) => {
							portal.portalDataGroups.push(pdg);
						});
					});
				});

				return pdgPromise.then(() => {
					return portal;
				});
			});

			// const pdgPromises: angular.IPromise<pms.PortalDataGroup>[] = [];

			//original.portalDataGroups.forEach((opdg) => {
			//	pdgPromises.push(this.copyAndSave_portalDataGroup(portal.id, portal.name, opdg));
			//});

			//return this.$q.all(pdgPromises).then((portalDataGroups) => {
			//	portal.portalDataGroups = portalDataGroups;
			//	return portal;
			//});
		}

		private copyAndSave_portalDataGroup(portalId: string, name: string, portalDataGroup: pms.PortalDataGroup): angular.IPromise<pms.PortalDataGroup> {
			const promise =
				this.$q.when(portalDataGroup.dataGroup.isPrivate ?
					this.copyAndSave_dataGroup(name, portalDataGroup.dataGroup) :
					portalDataGroup.dataGroup);

			return promise.then((response) => {
				const copy = new pms.PortalDataGroup(<pms.IPortalDataGroup>{
					portalId: portalId,
					dataGroupId: response.id,
					priority: portalDataGroup.priority
				});

				return this.portalDataGroupService.save(copy);
			});
		}

		private copyAndSave_dataGroup(name: string, dataGroup: pms.DataGroup): angular.IPromise<pms.DataGroup> {
			const copy = new pms.DataGroup(<pms.IDataGroup>{
				dataGroupTypeId: dataGroup.dataGroupTypeId,
				isPrivate: dataGroup.isPrivate,
				name: name
			});

			return this.dataGroupService.save(copy).then((response) => {
				let promise = this.$q.when();

				dataGroup.dataRows.forEach((dataRow) => {
					const dataValues: pms.DataValue[] = [];

					dataRow.dataValues.forEach((dataValue) => {
						const dvc = new pms.DataValue(<pms.IDataValue>{
							dataColumnId: dataValue.dataColumnId,
							value: dataValue.value
						});

						dataValues.push(dvc);
					});

					const drc = new pms.DataRow(<pms.IDataRow>{
						dataGroupId: response.id,
						dataTableId: dataRow.dataTableId,
						dataValues: dataValues,
						key: dataRow.key
					});

					promise = promise.then(() => {
						return this.dataRowService.save(drc).then(() => {
							// do nothing
						});
					});
				});

				return promise.then((responses) => {
					return response;
				});

				//const promises: angular.IPromise<pms.DataRow>[] = [];

				//dataGroup.dataRows.forEach((dataRow) => {
				//	const dataValues: pms.DataValue[] = [];

				//	dataRow.dataValues.forEach((dataValue) => {
				//		const dvc = new pms.DataValue(<pms.IDataValue>{
				//			dataColumnId: dataValue.dataColumnId,
				//			value: dataValue.value
				//		});

				//		dataValues.push(dvc);
				//	});

				//	const drc = new pms.DataRow(<pms.IDataRow>{
				//		dataGroupId: response.id,
				//		dataTableId: dataRow.dataTableId,
				//		dataValues: dataValues,
				//		key: dataRow.key
				//	});

				//	promises.push(this.dataRowService.save(drc));
				//});

				//return this.$q.all(promises).then((responses) => {
				//	return response;
				//});
			});
		}

		// portal child functions

		removePortalChild(portalChild: pms.PortalChild, portalChildren: pms.PortalChild[] = null): angular.IPromise<boolean> {
			return this.portalChildService.delete(portalChild).then((response: boolean) => {
				if (portalChildren) {
					for (let i = portalChildren.length - 1; i >= 0; i--) {
						if (portalChildren[i].id === portalChild.id) {
							portalChildren.splice(i, 1);
						}
					}
				}

				return response;
			});
		}

		savePortalChild(portalChild: pms.PortalChild, portalChildren: pms.PortalChild[] = null): angular.IPromise<pms.PortalChild> {
			let duplicate = false;

			// check for a duplicate portalChild childId
			if (portalChildren) {
				portalChildren.forEach((pc: pms.PortalChild) => {
					if (pc.id !== portalChild.id && pc.childId === portalChild.childId) {
						duplicate = true;
					}
				});

				if (duplicate) {
					return this.$q.when(null);
				}
			}

			// save portal child
			return this.portalChildService.save(portalChild).then((response: pms.PortalChild) => {
				if (portalChildren) {
					if (portalChild.id) {
						// update portal child
						for (let i = portalChildren.length - 1; i >= 0; i--) {
							if (portalChildren[i].id === portalChild.id) {
								portalChildren[i] = response;
							}
						}
					} else {
						// insert portal child
						portalChildren.push(response);
					}
				}

				return response;
			});
		}

		// portal data group functions

		flattenPortalDataGroups(portalDataGroups: pms.PortalDataGroup[]): angular.IPromise<boolean> {
			const sorted = portalDataGroups.sort((a, b) => {
				return a.priority - b.priority;
			});

			const keepers = sorted.filter((portalDataGroup) => {
				return portalDataGroup.dataGroup.isPrivate;
			});

			let promises: angular.IPromise<any>[] = [];

			sorted.forEach((portalDataGroup) => {
				promises.push(this.dataRowService.getByDataGroupId(portalDataGroup.dataGroup.id).then((response) => {
					portalDataGroup.dataGroup.dataRows = response;
					return true;
				}));
			});

			return this.$q.all(promises).then(() => {
				promises = [];

				sorted.forEach((portalDataGroup) => {
					if (!portalDataGroup.dataGroup.isPrivate) {
						portalDataGroup.dataGroup.dataRows.forEach((dataRow) => {
							const matches = keepers[0].dataGroup.getDataRows(dataRow.dataTableId, dataRow.key);

							if (matches.length === 0) {
								const dataValues: pms.DataValue[] = [];

								dataRow.dataValues.forEach((dataValue) => {
									const dvc = new pms.DataValue(<pms.IDataValue>{
										dataColumnId: dataValue.dataColumnId,
										value: dataValue.value
									});

									dataValues.push(dvc);
								});

								const drc = new pms.DataRow(<pms.IDataRow>{
									dataGroupId: keepers[0].dataGroup.id,
									dataTableId: dataRow.dataTableId,
									dataValues: dataValues,
									key: dataRow.key
								});

								keepers[0].dataGroup.dataRows.push(drc);

								promises.push(this.dataRowService.save(drc));
							}
						});

						promises.push(this.removePortalDataGroup(portalDataGroup));
					}
				});

				return this.$q.all(promises).then(() => {
					angular.copy(keepers, portalDataGroups);
					return true;
				});
			});
		}

		removePortalDataGroup(portalDataGroup: pms.PortalDataGroup, portalDataGroups: pms.PortalDataGroup[] = null): angular.IPromise<boolean> {
			return this.portalDataGroupService.delete(portalDataGroup).then((response: boolean) => {
				if (portalDataGroups) {
					for (let i = portalDataGroups.length - 1; i >= 0; i--) {
						if (portalDataGroups[i].id === portalDataGroup.id) {
							portalDataGroups.splice(i, 1);
						}
					}
				}

				return response;
			});
		}

		savePortalDataGroup(portalDataGroup: pms.PortalDataGroup, portalDataGroups: pms.PortalDataGroup[] = null, dataGroups: pms.DataGroup[] = null): angular.IPromise<pms.PortalDataGroup> {
			let duplicate = false;

			// check for a duplicate portalDataGroup dataGroupId or priority
			if (portalDataGroups) {
				portalDataGroups.forEach((pdg: pms.PortalDataGroup) => {
					if (pdg.id !== portalDataGroup.id && (pdg.dataGroupId === portalDataGroup.dataGroupId || pdg.priority === portalDataGroup.priority)) {
						duplicate = true;
					}
				});

				if (duplicate) {
					return this.$q.when(null);
				}
			}

			// save portalDataGroup
			return this.portalDataGroupService.save(portalDataGroup).then((response: pms.PortalDataGroup) => {
				if (portalDataGroups) {
					if (portalDataGroup.id) {
						// update portalDataGroup
						for (let i = portalDataGroups.length - 1; i >= 0; i--) {
							if (portalDataGroups[i].id === portalDataGroup.id) {
								portalDataGroups[i] = response;
							}
						}
					} else {
						// insert portalDataGroup
						portalDataGroups.push(response);
					}
				}

				if (dataGroups) {
					var matches = dataGroups.filter((dataGroup: pms.DataGroup) => {
						return dataGroup.id === portalDataGroup.dataGroupId;
					});

					response.dataGroup = matches[0];
				}

				return response;
			});
		}

		// portal group functions

		removePortalGroup(portalGroup: pms.PortalGroup, portalGroups: pms.PortalGroup[] = null): angular.IPromise<boolean> {
			return this.portalGroupService.delete(portalGroup).then((response: boolean) => {
				if (portalGroups) {
					for (let i = portalGroups.length - 1; i >= 0; i--) {
						if (portalGroups[i].id === portalGroup.id) {
							portalGroups.splice(i, 1);
						}
					}
				}

				return response;
			});
		}

		savePortalGroup(portalGroup: pms.PortalGroup, portalGroups: pms.PortalGroup[] = null): angular.IPromise<pms.PortalGroup> {
			let duplicate = false;

			// check for a duplicate portalGroup name
			if (portalGroups) {
				portalGroups.forEach((pg: pms.PortalGroup) => {
					if (pg.id !== portalGroup.id && pg.name === portalGroup.name) {
						duplicate = true;
					}
				});

				if (duplicate) {
					return this.$q.when(null);
				}
			}

			// save portalGroup
			return this.portalGroupService.save(portalGroup).then((response: pms.PortalGroup) => {
				if (portalGroups) {
					if (portalGroup.id) {
						// update portalGroup
						for (let i = portalGroups.length - 1; i >= 0; i--) {
							if (portalGroups[i].id === portalGroup.id) {
								portalGroups[i] = response;
							}
						}
					} else {
						// insert portalGroup
						portalGroups.push(response);
					}
				}

				return response;
			});
		}
	}
}